<template>
  <div>
    <ConfirmExit
      v-if="isLeavingRoute"
      v-on:isLeavingRoute="$emit('proceedLeave')"
      v-on:isNotLeavingRoute="$emit('cancelLeave')"
    />
    <RoleHeader
      :title="resource.title"
      :svgSource="getSvgIconSrc('Roles')"
      :buttons="buttons"
    />
    <v-row class="mt-6">
      <v-col cols="8">
        <RelatedPartyList
          :refreshKey="refreshPartyListKey"
          @onRowClick="handleRowClick"
        />
      </v-col>
      <v-col cols="4">
        <RolesAssignedList
          v-show="!displayRoleDetails"
          :customerId="selectedPartyId"
          @rolesChanged="onRolesChanged"
          @viewPermissions="onViewPermissions"
        />
        <RolePermissionList
          v-if="displayRoleDetails"
          :showBackButton="true"
          :corporateCode="selectedRole.defaultRoleFlag === 'Y' ? 'GLOBAL' : corporateId"
          :roleCode="selectedRole.roleCode"
          @back="displayRoleDetails = false"
        />
      </v-col>
    </v-row>
    <Loader v-show="loaderEnabled"/>
    <Modal :config="modalConfig" />
    <OtpHandler
      v-if="shouldShowOtpScreen"
      @otp-cancelled="shouldShowOtpScreen = false"
      @otp-success="assignRoles"
    />
  </div>
</template>
<script>
import '@/styles/pages/roles/roles.scss'
import CommonService from '@/service/commonService.js'
import Loader from '@/components/common/Loader.vue'
import preload from '@/util/preload'
import RoleHeader from './RoleHeader.vue'
import RelatedPartyList from './RelatedPartyList.vue'
import RolesAssignedList from './RolesAssignedList.vue'

const Modal = {
  ASSIGN_SUCCES: 1,
  ASSIGN_FAILED: 2,
  ASSIGN_CANCEL: 3
}

export default {
  name: 'AssignRoles',
  components: {
    RoleHeader,
    RolesAssignedList,
    RelatedPartyList,
    Loader,
    ConfirmExit: () => import('@/components/common/ConfirmExit.vue'),
    RolePermissionList: () => import('./RolePermissionList.vue'),
    Modal: () => import('@/components/common/Modal.vue'),
    OtpHandler: () => import('@/components/common/OtpHandler.vue')
  },
  data () {
    return {
      Modal,
      loaderEnabled: false,
      displayRoleDetails: false,
      isPristine: true,
      hasExceededLimit: false,
      isLeavingRoute: false,
      listOfSmeRoles: [],
      refreshPartyListKey: 0,
      modalConfig: {},
      selectedRole: null,
      selectedPartyId: null,
      hasSuccessfulUpdate: false,
      shouldShowOtpScreen: false
    }
  },
  computed: {
    username: () => JSON.parse(sessionStorage.getItem('username')),
    corporateId: () => JSON.parse(sessionStorage.getItem('corporateId')),
    showButton () {
      if (this.displayRoleDetails) return false
      if (!this.selectedPartyId) return false
      return true
    },
    resource () {
      return {
        title: this.$t('smeRoles.title'),
        cancelRoleAssign: this.$t('smeRoles.discardChanges'),
        viewRoleAssigngments: this.$t('smeRoles.viewRoleAssignments'),
        gotoDashboard: this.$t('common.goToDashboardBtnLabel'),
        roleAssignSuccess: this.$t('smeRoles.roleAssignSuccess'),
        roleAssignFail: this.$t('smeRoles.roleAssignFail'),
        roleAssignSuccessText: this.$t('smeRoles.roleAssignSuccessText'),
        failSubtitle1: this.$t('smeRoles.failSubtitle1'),
        failSubtitle2: this.$t('smeRoles.failSubtitle2'),
        discardUnsavedChanges: this.$t('common.discardUnsavedChanges'),
        confirmDiscardMessage: this.$t('common.confirmDiscardMessage'),
        button: {
          cancel: this.$t('common.cancelButton_text'),
          save: this.$t('common.saveButton_text'),
          no: this.$t('common.no'),
          yes: this.$t('common.yes')
        }
      }
    },
    buttons () {
      return [
        {
          label: this.resource.button.cancel,
          show: this.showButton,
          action: this.cancel
        },
        {
          label: this.resource.button.save,
          show: this.showButton,
          action: this.onAssignRoles,
          disabled: this.isPristine || this.hasExceededLimit
        }
      ]
    }
  },
  methods: {
    init () {
      this.displayRoleDetails = false
      this.listOfSmeRoles = []
      this.selectedPartyId = null
      this.selectedRole = null
      this.refreshPartyListKey++
      this.isPristine = true
      this.hasExceededLimit = false
      this.shouldShowOtpScreen = false
    },
    onAssignRoles () {
      const enableOtp = this.$appSettings.defaultConfigurations.smeRoles.enableOtpForRoleAssignment
      if (enableOtp) {
        this.shouldShowOtpScreen = true
        return
      }
      this.assignRoles()
    },
    cancel () {
      if (!this.isPristine) {
        this.showModal(Modal.ASSIGN_CANCEL)
      } else {
        this.init()
      }
    },
    handleRowClick (item) {
      if (this.selectedPartyId !== item.relatedPartyId) {
        this.isPristine = true
      }
      this.selectedPartyId = item.relatedPartyId
    },
    async returnToDashboard () {
      await preload.getListOfAccounts(this.username)
      this.$router.push('/home')
    },
    async assignRoles () {
      this.shouldShowOtpScreen = false
      const body = {
        customerId: this.selectedPartyId,
        corporateCode: this.corporateId,
        dehRoleDetails: this.listOfSmeRoles,
        createdByCustomerId: JSON.parse(sessionStorage.getItem('customerCode'))
      }
      const response = await CommonService.assignSmeRoles(body)
      if (response && !response.causes) {
        this.hasSuccessfulUpdate = true
        this.showModal(Modal.ASSIGN_SUCCES)
      }
    },
    onRolesChanged (event) {
      const { listOfSmeRoles, hasExceededLimit, isPristine } = event
      this.hasExceededLimit = hasExceededLimit
      this.listOfSmeRoles = listOfSmeRoles
      this.isPristine = isPristine
    },
    onViewPermissions (role) {
      this.displayRoleDetails = true
      this.selectedRole = role
    },
    showModal (modal) {
      const { resource: t } = this
      this.modalConfig = {
        enabled: true,
        width: '500px'
      }
      const modalConfigMap = {
        [Modal.ASSIGN_SUCCES]: {
          title: t.roleAssignSuccess,
          subtitle: t.roleAssignSuccessText,
          btn1Label: t.viewRoleAssigngments,
          btn1Callback: this.init,
          btn2Label: t.gotoDashboard,
          btn2Callback: this.returnToDashboard
        },
        [Modal.ASSIGN_FAILED]: {
          title: t.roleAssignFail,
          subtitle: `${t.failSubtitle1}${t.failSubtitle2}`,
          btn1Label: t.viewRoleAssigngments,
          btn1Callback: this.init,
          btn2Label: t.gotoDashboard,
          btn2Callback: this.returnToDashboard
        },
        [Modal.ASSIGN_CANCEL]: {
          title: t.discardUnsavedChanges,
          subtitle: t.confirmDiscardMessage,
          btn1Label: t.button.yes,
          btn1Callback: this.init,
          btn2Label: t.button.no
        }
      }
      Object.assign(this.modalConfig, modalConfigMap[modal] || {})
    }
  },
  beforeRouteLeave (to, from, next) {
    if (this.isPristine || this.hasSuccessfulUpdate) {
      next()
    }
    this.isLeavingRoute = true
    this.$once('proceedLeave', () => {
      next()
    })
    this.$once('cancelLeave', () => {
      this.isLeavingRoute = false
      next(false)
    })
  }
}
</script>
